export class DownloadHelper {
    /**
     * Detect if the current device is iOS
     * @returns {boolean}
     */
    static isIOSDevice() {
        const userAgent = navigator.userAgent;
        return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    }

    /**
     * Download a file from the specified URL
     * @param {string} url - Download URL
     * @param {string} filename - Filename for download
     * @returns {Promise}
     */
    static download(url, filename) {
        return new Promise((resolve, reject) => {
            const isIOS = this.isIOSDevice();

            if (isIOS) {
                // iOS Safari has strict limitations on programmatic downloads
                // The best approach is to open the URL directly in a new window/tab
                // This preserves the user gesture and allows the browser to handle the download
                try {
                    const newWindow = window.open(url, '_blank');

                    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                        window.location.href = url;
                    }

                    resolve({ success: true, message: 'Download initiated successfully' });
                } catch (fallbackError) {
                    reject(fallbackError);
                }
            } else {
                // setTimeout is required as a workaround for this issue:
                //
                // "Fetch requests inside onClick abort when downloading a file in Firefox"
                // https://stackoverflow.com/questions/57542425/fetch-requests-inside-onclick-abort-when-downloading-a-file-in-firefox
                //
                // This allows us to record device events and download files simultaneously.
                // This issue affects Firefox and Safari browsers.
                setTimeout(() => {
                    try {
                        this.createAndTriggerDownload(url, filename);
                        resolve({ success: true, message: 'Download completed successfully' });
                    } catch (error) {
                        reject(error);
                    }
                }, 10);
            }
        });
    }

    /**
     * Creates an anchor element and triggers a download
     * @param {string} downloadUrl - URL to download from
     * @param {string} filename - Filename for the download (optional)
     */
    static createAndTriggerDownload(downloadUrl, filename = null) {
        const downloadElement = document.createElement('a');
        downloadElement.href = downloadUrl;

        if (filename) {
            downloadElement.download = filename;
        }

        downloadElement.style.display = 'none';
        document.body.appendChild(downloadElement);
        downloadElement.click();
        document.body.removeChild(downloadElement);
    }

    /**
     * Generic download handler with persistent toast
     * @param {string} url - Download URL
     * @param {string} filename - Filename for download
     * @param {Object} toastService - Toast service instance
     * @param {function} onSuccess - Success callback
     * @param {function} onError - Error callback (optional)
     * @param {string} startMessage - Toast message when download starts
     * @param {Object} i18next - i18next instance for translations
     */
    static async downloadWithToast(url, filename, toastService, onSuccess, onError = null, startMessage = null, i18next = null) {
        const defaultStartMessage =
            startMessage || (i18next ? i18next.t('download-as-one-started-toast') : null) || 'Preparing download...';

        toastService.showPersistentToast(defaultStartMessage, 'info');

        try {
            return this.download(url, filename)
                .then(() => {
                    if (onSuccess) onSuccess();
                    toastService.hidePersistentToast();
                    const successMessage = (i18next ? i18next.t('download-complete') : null) || 'Download ready!';
                    toastService.showToast(successMessage, 'success');
                })
                .catch((error) => {
                    if (onError) onError(error);
                    toastService.hidePersistentToast();
                    const errorMessage = (i18next ? i18next.t('download-error') : null) || 'Download failed';
                    toastService.showToast(errorMessage, 'error');
                    throw error;
                });
        } catch (error) {
            toastService.hidePersistentToast();
            throw error;
        }
    }
}
